import React, {useContext, useState} from "react"
import {Alert, Box, Button, Card, Chip, Stack, Tooltip, Typography} from "@mui/material"
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined"
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined"
import SupportAgentIcon from "@mui/icons-material/SupportAgent"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined"
import {useApproveAiAnswerMutation, useRejectAiAnswerMutation} from "../../core/api/ai-zendesk-api"
import {SnackBarContext} from "../../snackBarProvider"
import {
    ANSWER_HINT,
    ANSWER_STATUS_COLOR,
    ANSWER_STATUS_LABEL,
    answerTitle,
    decisionLabel,
    errorText,
    formatExact,
    formatTime,
    isPendingAnswer,
    isUnsentAnswer,
    reviewVersion,
    ruleKeyLabel
} from "./aiTicketsUtils"
import {ApproveDialog, RejectDialog} from "./DecisionDialogs"

const Meta = ({children}) => (
    <Typography variant="caption" color="text.secondary" component="div">{children}</Typography>
)

/** Bot decided to hand the ticket to a human — shown as an action in the history, nothing to approve. */
const EscalationRow = ({answer}) => (
    <Stack direction="row" spacing={0.75} sx={{alignItems: "flex-start", px: 0.5}}>
        <SupportAgentIcon fontSize="small" color="warning" sx={{mt: 0.25}}/>
        <Box sx={{minWidth: 0}}>
            <Typography variant="caption" component="div" sx={{fontWeight: 600}}>
                Escalated to operator
                {answer.category ? ` · ${answer.category}` : ""}
            </Typography>
            <Meta>
                {formatTime(answer.createdAt)}
                {answer.status ? ` · ${ANSWER_STATUS_LABEL[answer.status] ?? answer.status.toLowerCase()}` : ""}
                {answer.source && answer.source !== "AI" ? ` · ${answer.source.toLowerCase()}` : ""}
                {answer.ruleKey ? ` · rule ${answer.ruleKey}` : ""}
                {answer.decision ? ` · ${decisionLabel(answer.decision)}` : ""}
            </Meta>
            {answer.evidence && (
                <Typography variant="caption" component="div" sx={{mt: 0.25, whiteSpace: "pre-wrap", wordBreak: "break-word"}}>
                    Hermes: {answer.evidence}
                </Typography>
            )}
        </Box>
    </Stack>
)

/**
 * Prepared reply (AI or template). PENDING_APPROVAL gets Approve (mark only) / Approve & send / Reject;
 * APPROVED (marked, not sent) gets Send to player / Reject; every other status is history
 * (who decided, when it was sent, why it failed).
 *
 * `context` comes from buildAnswerContext: step/total within the answers for one player message and
 * `blockedBy` — the lower-sequence draft that must be approved first (each approve sends one message).
 */
export default function AnswerCard({answer, ticketId, context}) {
    const {addAlert} = useContext(SnackBarContext)
    const [dialog, setDialog] = useState(null) // "approve" | "reject" | null
    const [lastError, setLastError] = useState(null)

    const [approve, {isLoading: approving}] = useApproveAiAnswerMutation()
    const [reject, {isLoading: rejecting}] = useRejectAiAnswerMutation()

    if (answer.type === "ESCALATION") return <EscalationRow answer={answer}/>

    const pending = isPendingAnswer(answer)
    const unsent = isUnsentAnswer(answer) // PENDING_APPROVAL or APPROVED: can still be sent or rejected
    const statusColor = ANSWER_STATUS_COLOR[answer.status] ?? "default"
    const isTemplate = answer.source === "TEMPLATE"
    const rule = ruleKeyLabel(answer.ruleKey)
    const review = reviewVersion(answer)
    const step = context && context.total > 1 ? context : null
    const blockedBy = context?.blockedBy ?? null
    const Icon = isTemplate ? ArticleOutlinedIcon : SmartToyOutlinedIcon

    const run = async (action, successText) => {
        setLastError(null)
        try {
            await action.unwrap()
            addAlert({text: successText, type: "success"})
            setDialog(null)
        } catch (error) {
            // 500 on approve = accepted but Zendesk send failed (status becomes SEND_FAILED); no retry
            setLastError(errorText(error))
            setDialog(null)
        }
    }

    return (
        <Card
            sx={{
                p: 1.25,
                borderColor: pending ? "warning.main" : `${statusColor === "default" ? "divider" : `${statusColor}.light`}`,
                borderWidth: pending ? 2 : 1,
                bgcolor: pending ? "background.paper" : "grey.50"
            }}
        >
            <Stack direction="row" spacing={0.5} sx={{alignItems: "center", mb: 0.25}}>
                <Icon fontSize="small" color={pending ? "primary" : "action"}/>
                <Typography variant="caption" sx={{fontWeight: 600, flex: 1}}>
                    {answerTitle(answer)}
                </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{alignItems: "center", flexWrap: "wrap", rowGap: 0.5, mb: 0.5}}>
                <Chip
                    label={ANSWER_STATUS_LABEL[answer.status] ?? answer.status}
                    color={statusColor}
                    sx={{height: 18, fontSize: 11}}
                />
                {step && (
                    <Chip
                        label={`step ${step.step} of ${step.total}`}
                        variant="outlined"
                        color={blockedBy ? "default" : "primary"}
                        sx={{height: 18, fontSize: 11}}
                    />
                )}
                {answer.withGreeting && <Chip label="starts with greeting" variant="outlined" sx={{height: 18, fontSize: 11}}/>}
                {answer.internalNoteAt && (
                    <Tooltip title={`Posted to the Zendesk ticket as an internal note (player does not see it) · ${formatExact(answer.internalNoteAt)}`}>
                        <Chip label="internal note in Zendesk" variant="outlined" sx={{height: 18, fontSize: 11}}/>
                    </Tooltip>
                )}
                {answer.internalNoteError && (
                    <Tooltip title={answer.internalNoteError}>
                        <Chip label="internal note failed" color="error" variant="outlined" sx={{height: 18, fontSize: 11}}/>
                    </Tooltip>
                )}
                {answer.status === "SENT" && <CheckCircleOutlineIcon fontSize="small" color="success"/>}
            </Stack>
            {rule && (
                <Typography variant="caption" component="div" sx={{fontWeight: 600, mb: 0.25}}>
                    {rule}
                </Typography>
            )}
            {ANSWER_HINT[answer.status] && (
                <Typography variant="caption" color={pending ? "warning.dark" : "text.secondary"} component="div" sx={{mb: 0.5}}>
                    {ANSWER_HINT[answer.status]}
                </Typography>
            )}

            {answer.ticketStatusAfterSend && (
                <Chip
                    label={`after send: ticket → ${answer.ticketStatusAfterSend}`}
                    color="info"
                    variant="outlined"
                    sx={{height: 18, fontSize: 11, mb: 0.5}}
                />
            )}
            <Meta>
                {formatTime(answer.createdAt)}
                {answer.decision ? ` · ${decisionLabel(answer.decision)}` : ""}
                {answer.strategy ? ` · ${answer.strategy.toLowerCase().replace("_", " ")}` : ""}
                {answer.source ? ` · ${answer.source.toLowerCase()}` : ""}
                {answer.category ? ` · ${answer.category}` : ""}
                {answer.language ? ` · ${answer.language}` : ""}
            </Meta>

            <Typography
                variant="body2"
                component="div"
                sx={{mt: 0.75, whiteSpace: "pre-wrap", wordBreak: "break-word", color: answer.status === "REJECTED" || answer.status === "SUPERSEDED" ? "text.disabled" : "text.primary"}}
            >
                {answer.text}
            </Typography>
            {review && (
                <Box sx={{mt: 0.75, p: 1, borderRadius: 1, bgcolor: "grey.100", borderLeft: 3, borderColor: "divider"}}>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{fontWeight: 600}}>
                        {review.label} (for you, the player gets the text above)
                    </Typography>
                    <Typography variant="body2" component="div" sx={{whiteSpace: "pre-wrap", wordBreak: "break-word", color: "text.secondary"}}>
                        {review.text}
                    </Typography>
                </Box>
            )}

            {(answer.decidedBy || answer.decidedAt || answer.sentAt) && (
                <Meta>
                    <Box component="span" sx={{display: "block", mt: 0.75}}>
                        {answer.decidedBy ? `${answer.status === "REJECTED" ? "Rejected" : "Approved"} by ${answer.decidedBy}` : ""}
                        {answer.decidedAt ? ` · ${formatExact(answer.decidedAt)}` : ""}
                    </Box>
                    {answer.sentAt && <Box component="span" sx={{display: "block"}}>Sent {formatExact(answer.sentAt)}</Box>}
                </Meta>
            )}

            {answer.evidence && (
                <Box sx={{mt: 0.75, p: 1, borderRadius: 1, bgcolor: "info.50", borderLeft: 3, borderColor: "info.light"}}>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{fontWeight: 600}}>
                        Hermes check (what the bot verified before deciding)
                    </Typography>
                    <Typography variant="body2" component="div" sx={{whiteSpace: "pre-wrap", wordBreak: "break-word"}}>
                        {answer.evidence}
                    </Typography>
                </Box>
            )}
            {answer.failReason && (
                <Alert severity={answer.status === "SEND_FAILED" ? "error" : "info"} sx={{mt: 0.75, py: 0, fontSize: 12}}>
                    {answer.failReason}
                </Alert>
            )}
            {lastError && (
                <Alert severity="error" sx={{mt: 0.75, py: 0, fontSize: 12}} onClose={() => setLastError(null)}>
                    {lastError}
                </Alert>
            )}

            {unsent && (
                <>
                    <Stack direction="row" spacing={1} sx={{mt: 1}}>
                        {pending && (
                            <Button
                                color="success"
                                variant="outlined"
                                fullWidth
                                onClick={() => run(approve({answerId: answer.id, ticketId, send: false}), `Answer for ticket #${ticketId} approved (not sent)`)}
                                disabled={approving || rejecting}
                            >
                                Approve
                            </Button>
                        )}
                        <Button color="success" variant="contained" fullWidth onClick={() => setDialog("approve")} disabled={approving || rejecting || Boolean(blockedBy)}>
                            {pending ? "Approve & send" : "Send to player"}
                        </Button>
                        <Button color="warning" variant="outlined" fullWidth onClick={() => setDialog("reject")} disabled={approving || rejecting}>
                            Reject
                        </Button>
                    </Stack>
                    {blockedBy && (
                        <Typography variant="caption" color="warning.dark" component="div" sx={{mt: 0.5}}>
                            Send step {context.step - 1} first{ruleKeyLabel(blockedBy.ruleKey) ? ` (${ruleKeyLabel(blockedBy.ruleKey).toLowerCase()})` : ""}: messages go to the player in order, one per send. "Approve" alone does not send anything.
                        </Typography>
                    )}
                </>
            )}

            <ApproveDialog
                open={dialog === "approve"}
                ticketId={ticketId}
                answer={answer}
                loading={approving}
                onClose={() => setDialog(null)}
                onConfirm={() => run(approve({answerId: answer.id, ticketId, send: true}), `Answer sent to ticket #${ticketId}`)}
            />
            <RejectDialog
                open={dialog === "reject"}
                ticketId={ticketId}
                loading={rejecting}
                onClose={() => setDialog(null)}
                onConfirm={(reason) => run(reject({answerId: answer.id, ticketId, reason}), `Answer for ticket #${ticketId} rejected`)}
            />
        </Card>
    )
}
